import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { FC, useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import PopupTechListItem from './components/PopupTechList';
import AddTechnicianModalToObject from './AddTechnicanToObjectModal/AddTechnicianToObjectModal';
import { useAddTechnicianToObjectModalStore } from './AddTechnicanToObjectModal/AddTechnicianToObjectModalStore';
import DeleteTechnicianModal from './DeleteTechnicianModal/DeleteTechnicianModal';
import { useDeleteTechnicianModalStore } from './DeleteTechnicianModal/DeleteTechnicianModalStore';

import { addTechnicianForClient } from 'api/clients/clientSmartCreation/addTechnicianForClient';
import { Client, GroupedObject, siteFormSchema, SiteFormValues } from 'api/clients/types';
import { MLInput } from 'components/Input/Input';
import useGetObject from 'hooks/Object/useGetObject';
import useUpdateObject from 'hooks/Object/useUpdateObject';
import { MLSelect } from 'components/Select/Select';
import GreenAddButton from 'components/Button/GreenAddButton';
import CreateSiteContactModal from 'pages/ClientDashboard/Modals/CreateSiteContantModal';
import useGetAvailableTechnicians from 'hooks/Object/useGetAvailableTechnicians';
import useGetAvailableManagers from 'hooks/Object/useGetAvailableManager';
import CreateEmployeeModal from 'pages/ClientDashboard/Modals/CreateEmployeeModal';
import useCreateSite from 'hooks/Object/useCreateSite';
import CreateTechnicianModal from 'pages/ClientDashboard/Modals/CreateTechnicianModal';
import { countries } from 'api/clients/country-list';

interface ManageObjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  refetchClients: () => void;
  objectId?: number;
  rootClient?: Client | GroupedObject;
}

interface NewTechnician {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  confirm_password: string;
}

type ObjectFormWithNewTech = SiteFormValues & {
  newTechnician?: NewTechnician;
};

const objectDefaultValues: SiteFormValues = {
  name: '',
  city: '',
  street: '',
  building_number: '',
  full_name: '',
  apartment_no: '',
  manager: 0,
  company: 0,
  technicians: [],
  zip_code: '',
  country: '',
};

const emptyNewTechnician: NewTechnician = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
  confirm_password: '',
};

const ManageObjectModal: FC<ManageObjectModalProps> = ({ isOpen, onClose, objectId, rootClient, refetchClients }) => {
  const { t } = useTranslation();
  const { isDeleteOnObjectModal } = useDeleteTechnicianModalStore();

  const { data, refetch: refetchObject } = useGetObject(objectId);

  const { data: availableTechnicians, refetch: refetchAvailableTechnicians } = useGetAvailableTechnicians();

  const [isEmployeeModalOpen, setIsEmployeeModalOpen] = useState(false);
  const { createSite, isLoading: isCreatingSite } = useCreateSite();

  const { updateObject, isLoading: isUpdating } = useUpdateObject();
  const { data: availableManagers, refetch: refetchAvailableManagers } = useGetAvailableManagers();

  const [isAddSiteContactOpen, setIsAddSiteContactOpen] = useState<boolean>(false);
  const [isAddTechnicianToNewSite, setIsAddTechnicianToNewSite] = useState<boolean>(false);
  const [isAddTechnicianToObjectModal, setIsAddTechnicianToObjectModal] = useState<boolean>(false);
  const { open: openAddTechnicianToObjectModalStore } = useAddTechnicianToObjectModalStore();

  const [isAddTechnician, setIsAddTechnician] = useState<boolean>(false);

  const { mutateAsync: createTechnician } = useMutation((payload: any) => addTechnicianForClient(payload));

  const technicianOptions = useMemo(() => {
    if (!availableTechnicians || availableTechnicians.length === 0) {
      return [{ value: '', label: t('Modals.manage-client.noTechniciansAvailable') }];
    }
    return [{ value: '', label: t('Modals.manage-object.selectTechnician') }].concat(
      availableTechnicians.map(tp => ({
        value: String(tp.id),
        label: tp.label || tp.email,
      })),
    );
  }, [availableTechnicians, t]);
  const managerOptions = useMemo(() => {
    if (!availableManagers || availableManagers.length === 0) {
      return [{ value: '', label: t('Modals.manage-client.noManagersAvailable') }];
    }
    return [{ value: '', label: t('Modals.manage-object.selectManager') }].concat(
      availableManagers.map(manager => ({
        value: String(manager.id),
        label: manager.first_name && manager.last_name ? `${manager.first_name} ${manager.last_name}` : manager.email,
      })),
    );
  }, [availableManagers, t]);
  // ---------- schema (create mode = same logic as ChooseModal) ----------
  const dynamicSchema = useMemo(() => {
    // Create mode
    if (!isAddTechnician) {
      const base = siteFormSchema.extend({
        technicians: z.array(z.number()).min(1, { message: t('Modals.manage-object.fieldRequired') as any }),
      });
      // require selecting technician (at least 1 id in technicians[])
      return base;
    }
    const base = siteFormSchema;
    // inline create => technicians optional, but newTechnician required
    return base.extend({
      technicians: z.array(z.number()).optional().default([]),
      newTechnician: z
        .object({
          first_name: z.string().min(1, t('Modals.manage-object.fieldRequired') as any),
          last_name: z.string().min(1, t('Modals.manage-object.fieldRequired') as any),
          email: z.string().email(t('Invalid email address' as any)),
          password: z.string().min(6, t('Password is too short' as any)),
          confirm_password: z.string().min(6),
        })
        .refine(d => d.password === d.confirm_password, {
          message: t("Passwords don't match" as any),
          path: ['confirm_password'],
        }),
    });
  }, [isAddTechnician, t]);
  const countryList = useMemo(() => {
    if (countries.length === 0 || !countries) {
      return [{ value: '', label: t('Modals.dont-have-country') }];
    }
    return [{ value: '', label: t('Modals.country-select') }].concat(
      countries.map(country => {
        return {
          value: String(country.id),
          label: country.countryName,
          flagImg: country.flagImg,
        };
      }),
    );
  }, [t]);
  const formatCountryOption = (option: any) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {option.flagImg && (
        <img src={option.flagImg} alt={option.label} style={{ width: '20px', height: '15px', display: 'block' }} />
      )}
      <span>{option.label}</span>
    </div>
  );
  const { handleSubmit, control, reset, setError, setValue } = useForm<ObjectFormWithNewTech>({
    resolver: zodResolver(dynamicSchema),
    defaultValues: {
      ...objectDefaultValues,
      newTechnician: emptyNewTechnician,
    },
  });

  const onReset = () => {
    reset({
      ...objectDefaultValues,
      company: rootClient?.id,
      manager: undefined,
      technicians: [],
      newTechnician: emptyNewTechnician,
      zip_code: '',
      country: '',
    });
    setIsAddTechnician(false);
  };

  const onCreateNewObject = async (form: ObjectFormWithNewTech) => {
    const companyId = rootClient?.id;
    if (!companyId) {
      enqueueSnackbar('Company is missing', { variant: 'error', style: { marginTop: '-10px' } });
      return;
    }

    try {
      let techniciansIds = form.technicians ?? [];

      if (!objectId && isAddTechnician) {
        const created: any = await createTechnician(form.newTechnician);
        techniciansIds = [Number(created.id)];
      }

      const payload: SiteFormValues = {
        name: form.name,
        city: form.city,
        street: form.street,
        building_number: form.building_number,
        apartment_no: form.apartment_no ?? '',
        zip_code: form.zip_code ?? '',
        country: form.country ?? '',
        full_name: form.name,
        company: companyId,
        manager: form.manager,
        technicians: techniciansIds,
      };

      createSite(payload, {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-create', { objectName: payload.name }), {
            variant: 'success',
            style: { marginTop: '-10px' },
          });
          onClose();
          onReset();
          refetchClients();
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.technician?.[0] || error?.response?.data?.detail || 'Error';
          enqueueSnackbar(msg, { variant: 'error', style: { marginTop: '-10px' } });
          if (error?.response?.data?.name) setError('name', { message: 'Name already exists' });
        },
      });
    } catch (e: any) {
      const msg = e?.response?.data?.detail || t('Modals.manage-technician.failCreate');
      enqueueSnackbar(msg, { variant: 'error', style: { marginTop: '-10px' } });
    }
  };

  const onUpdateObject = (form: SiteFormValues) => {
    const companyId = rootClient?.id;

    const payload: SiteFormValues = {
      ...form,
      full_name: form.name,
      company: (companyId ?? form.company ?? null) as any,
      technicians: form.technicians ?? [],
    };

    updateObject(
      {
        object: payload,
        id: objectId,
      },
      {
        onSuccess: () => {
          enqueueSnackbar(t('Modals.success-create', { objectName: payload.name }), {
            variant: 'success',
            style: { marginTop: '-10px' },
          });
          onClose();
          onReset();
          refetchClients();
        },
        onError: (error: any) => {
          const msg = error?.response?.data?.technician?.[0] || error?.response?.data?.detail || 'Error';
          enqueueSnackbar(msg, { variant: 'error', style: { marginTop: '-10px' } });

          if (error?.response?.data?.name) setError('name', { message: 'Name already exists' });
        },
      },
    );
  };

  // Populate form on open / switching edit->create
  useEffect(() => {
    if (objectId) {
      reset({
        name: data?.name ?? '',
        full_name: data?.full_name ?? '',
        city: data?.city ?? '',
        street: data?.street ?? '',
        building_number: data?.building_number ?? '',
        apartment_no: data?.apartment_no ?? '',
        company: data?.company ?? rootClient?.id ?? 0,
        technicians: (data?.technicians ?? [])
          .map(tch => tch?.technician?.id)
          .filter((id): id is number => typeof id === 'number'),
        // keep empty in edit mode
        newTechnician: emptyNewTechnician,
        manager: data?.manager ?? undefined,
      });
      setIsAddTechnician(false);
    } else {
      reset({
        ...objectDefaultValues,
        company: rootClient?.id ?? 0,
        manager: undefined,
        technicians: [],
        newTechnician: emptyNewTechnician,
      });
      setIsAddTechnician(false);
    }
  }, [objectId, reset, data, rootClient?.id]);

  // create-mode: when toggling inline tech creation, clear conflicting fields
  useEffect(() => {
    if (objectId) return; // only for create mode
    if (isAddTechnician) {
      setValue('technicians', [], { shouldValidate: true });
    } else {
      setValue('newTechnician', emptyNewTechnician, { shouldValidate: false });
    }
  }, [isAddTechnician, objectId, setValue]);
  const onFormError = (errors: any) => {
    if (errors.technicians) {
      enqueueSnackbar(t('Modals.manage-object.noTechWarning', 'Please select a technician!'), {
        variant: 'error',
      });
    }
  };
  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          onClose();
          onReset();
        }}
        size="lg"
      >
        <ModalHeader
          className="border-bottom py-3 "
          toggle={() => {
            onClose();
            onReset();
          }}
        >
          <div className="text-purple">
            {objectId
              ? `${t('Modals.manage-object.edit.title')}`
              : `${t('Modals.manage-object.create.title')} - "${rootClient?.name}"`}
          </div>
        </ModalHeader>

        <ModalBody>
          <div className="d-flex justify-content-center">
            <form className="w-100" onSubmit={e => e.preventDefault()}>
              <div className="row">
                <div className="mb-3 col-md-12 col-sm-12">
                  <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        type="text"
                        label={t('Modals.manage-client.short-name')}
                        error={error?.message}
                        required
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-4">
                  <Controller
                    control={control}
                    name="country"
                    render={({ field, fieldState: { error } }) => (
                      <MLSelect
                        {...field}
                        label={t('Modals.manage-client.country')}
                        error={error?.message}
                        options={countryList}
                        formatOptionLabel={formatCountryOption}
                        value={String(field.value || '')}
                        onChange={val => field.onChange(val || '')}
                        required
                      />
                    )}
                  />
                </div>
                <div className="mb-3 col-md-4">
                  <Controller
                    control={control}
                    name="city"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        type="text"
                        label={t('Modals.manage-client.city')}
                        error={error?.message}
                        required
                        {...field}
                      />
                    )}
                  />
                </div>
                <div className="mb-3 col-md-4">
                  <Controller
                    control={control}
                    name="zip_code"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        type="text"
                        label={t('Modals.manage-client.zip')}
                        error={error?.message}
                        required
                        {...field}
                        value={field.value ?? ''}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="mb-3 col-md-4">
                  <Controller
                    control={control}
                    name="street"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        type="text"
                        error={error?.message}
                        label={t('Modals.manage-client.street')}
                        required
                        {...field}
                      />
                    )}
                  />
                </div>

                <div className="mb-3 col-md-4">
                  <Controller
                    control={control}
                    name="building_number"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        error={error?.message}
                        type="text"
                        label={t('Modals.manage-client.building_no')}
                        required
                        {...field}
                      />
                    )}
                  />
                </div>

                <div className="mb-3 col-md-4">
                  <Controller
                    control={control}
                    name="apartment_no"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        type="text"
                        error={error?.message}
                        label={t('Modals.manage-client.apartment_no')}
                        {...field}
                      />
                    )}
                  />
                </div>
              </div>

              <div className="row gap-2">
                <h4 className="my-3 border-bottom pb-2">{t('Modals.manage-object.persons')}</h4>

                <div>
                  <div className="d-flex gap-2 justify-content-center align-items-center">
                    <div className="mb-4 col-md-11">
                      <Controller
                        control={control}
                        name="manager"
                        render={({ field, fieldState: { error } }) => (
                          <MLSelect
                            required
                            label={t('Modals.manage-technician.manager')}
                            error={error?.message}
                            options={managerOptions}
                            value={field.value != null ? String(field.value) : ''}
                            onChange={val => field.onChange(val ? Number(val) : null)}
                          />
                        )}
                      />
                    </div>

                    <GreenAddButton
                      title={t('Modals.manage-client.createNewManager')}
                      onClick={() => setIsEmployeeModalOpen(true)}
                    />
                  </div>
                </div>

                <div>
                  {objectId && (
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="title">{t('Modals.manage-object.techniques')}</div>
                      <GreenAddButton
                        title={t('Modals.manage-technician.technician')}
                        onClick={() => {
                          if (!data?.slug) return;
                          openAddTechnicianToObjectModalStore(data.slug);
                          setIsAddTechnicianToObjectModal(true);
                        }}
                      />
                    </div>
                  )}
                  {!objectId && (
                    <>
                      <div className="d-flex gap-2 justify-content-center align-items-center">
                        <div className="mb-4 col-md-11">
                          <Controller
                            control={control}
                            name="technicians"
                            render={({ field, fieldState: { error } }) => (
                              <MLSelect
                                required={!isAddTechnician}
                                isDisabled={isAddTechnician}
                                label={t('Modals.manage-technician.technician')}
                                error={error?.message}
                                options={technicianOptions}
                                value={field.value?.[0] != null ? String(field.value[0]) : ''}
                                onChange={val => field.onChange(val ? [Number(val)] : [])}
                              />
                            )}
                          />
                        </div>

                        <div className="col-md-1 flex-shrink-1">
                          <GreenAddButton
                            title={t('Modals.manage-object.createAndSelectTechnician')}
                            onClick={() => setIsAddTechnicianToNewSite(true)}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {objectId &&
                    data?.technicians?.map(tech => (
                      <PopupTechListItem
                        key={tech?.technician?.id}
                        siteTechnician={tech}
                        refetchObject={refetchObject}
                        objectSlug={data.slug}
                      />
                    ))}
                </div>
              </div>
            </form>
          </div>
        </ModalBody>

        <ModalFooter>
          <Button
            className="btn btn-ghost-danger"
            onClick={() => {
              onClose();
              onReset();
            }}
          >
            {t('Modals.delete-modal.cancel')}
          </Button>

          <Button
            className="btn btn-ghost-success"
            onClick={
              objectId ? handleSubmit(onUpdateObject, onFormError) : handleSubmit(onCreateNewObject, onFormError)
            }
            disabled={isCreatingSite || isUpdating}
          >
            {objectId ? t('Modals.delete-modal.edit') : t('Modals.delete-modal.create')}
          </Button>
        </ModalFooter>
      </Modal>

      <CreateSiteContactModal
        isOpen={isAddSiteContactOpen}
        onClose={() => {
          setIsAddSiteContactOpen(false);
        }}
      />
      <CreateEmployeeModal
        isAddingEmployee={!objectId}
        isOpen={isEmployeeModalOpen}
        onClose={() => {
          setIsEmployeeModalOpen(false);
          refetchAvailableManagers();
        }}
      />

      {isDeleteOnObjectModal && <DeleteTechnicianModal refetch={refetchObject} />}
      <CreateTechnicianModal
        isClientModal={false}
        isOpen={isAddTechnicianToNewSite}
        onClose={() => {
          setIsAddTechnicianToNewSite(false);
          refetchObject();
          refetchAvailableTechnicians();
        }}
      />
      <AddTechnicianModalToObject
        close={() => {
          setIsAddTechnicianToObjectModal(false);
          refetchObject();
        }}
        isOpen={isAddTechnicianToObjectModal}
      />
    </div>
  );
};

export default ManageObjectModal;
